import { TableHeaderButtons, TableHeaderCustomContent } from 'cdk-radial';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DownloadButton, EVENT_NAME, PrintButton } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { ExportOrArchiveDialog } from 'components/ExportOrArchiveDialog';
import { PrintViewer } from 'components/PrintViewer/PrintViewer';
import { getCompanyId } from 'components/StoreSelect/slice';
import { useSubmitAuthorizedPayment } from 'hooks';
import { usePrintButton } from 'hooks/usePrintButton';
import { getAuthorizeReviewRequest } from 'pages/AuthorizePaymentPage/selector';
import { AuthorizePaymentReviewType } from 'pages/AuthorizePaymentPage/types';
import { processRetryData } from 'pages/VendorPaymentResultPage/helpers';
import { TableHeaderType } from 'pages/VendorPaymentResultPage/interfaces';
import { labelMessages } from 'pages/VendorPaymentResultPage/messages';
import { getRecordsToRetry } from 'pages/VendorPaymentResultPage/selectors';
import {
  FailedGridOptionsStyle,
  IconErrorStyle,
  PaymentDetailTableHeaderContentStyle,
  RetryButtonStyle
} from 'pages/VendorPaymentResultPage/styled';
import { VendorPaymentDetails } from 'pages/VendorPaymentResultPage/types';
import { useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';

export const FailedTableHeader = ({
  tableHeaderTitle,
  totalRecord,
  gridRef,
  columnDefs
}: TableHeaderType): React.JSX.Element => {
  const intl = useIntl();
  const amplitude = useAmplitude();
  const companyId = useAppSelector(getCompanyId);
  const { mutate: retrySelected } = useSubmitAuthorizedPayment(true);
  const recordsToRetrySubmission = useAppSelector(getRecordsToRetry);
  const { paymentDate, postingDescription, cashInBankAccount } = useAppSelector(
    getAuthorizeReviewRequest
  );

  const titleForExport = `${intl.formatMessage(
    labelMessages.paymentResultPageHeader
  )}-${tableHeaderTitle} (${totalRecord})`;
  const [downloadClicked, setDownloadClicked] = useState<boolean>(false);

  const { displayPrinter, onPrintClick, onClose } = usePrintButton();
  const submitRetry = () => {
    if (recordsToRetrySubmission.length > 0) {
      const authorizationPaymentList = processRetryData(
        recordsToRetrySubmission
      );
      const authorizePaymentReview: AuthorizePaymentReviewType = {
        paymentDate,
        postingDescription,
        companyId,
        cashInBankAccount,
        authorizationPaymentList
      };

      retrySelected(authorizePaymentReview);
      amplitudeTracker(amplitude, EVENT_NAME.RetrySelected);
    }
  };

  const onDownloadClick = () => setDownloadClicked(true);

  const onDownloadClose = () => setDownloadClicked(false);

  return (
    <>
      {displayPrinter && (
        <PrintViewer<VendorPaymentDetails>
          gridRef={gridRef}
          open={displayPrinter}
          onClose={onClose}
          tableHeaderFilters={{ tableTitle: titleForExport }}
        />
      )}
      <TableHeaderCustomContent
        id="composed-table"
        customHeaderContent={
          <>
            <IconErrorStyle />
            <PaymentDetailTableHeaderContentStyle>
              {`${tableHeaderTitle} (${totalRecord})`}
            </PaymentDetailTableHeaderContentStyle>
            <FailedGridOptionsStyle>
              <RetryButtonStyle
                text={
                  <FormattedMessage
                    defaultMessage="Retry Selected"
                    description="retry button text to resubmit the failed records"
                  />
                }
                variant="secondary"
                onClick={submitRetry}
              />
              {downloadClicked && (
                <ExportOrArchiveDialog<VendorPaymentDetails>
                  isOpen={downloadClicked}
                  onClose={onDownloadClose}
                  gridRef={gridRef}
                  columnDefs={columnDefs}
                  tableHeaderFilters={{ tableTitle: titleForExport }}
                />
              )}
              <TableHeaderButtons
                id="tableSimple"
                onDownloadClick={onDownloadClick}
                onPrintClick={onPrintClick}
                downloadButtonText={DownloadButton.FailedFileDownload}
                printButtonText={PrintButton.FailedFilePrint}
                isSearchable={false}
              />
            </FailedGridOptionsStyle>
          </>
        }
      />
    </>
  );
};
